import { useMemo } from "react";
import {
  Paper,
  Text,
  Stack,
  Group,
  Loader,
  Center,
  Alert,
  Box,
  Tooltip as MantineTooltip,
  ScrollArea,
} from "@mantine/core";
import { IconAlertTriangle, IconBrain } from "@tabler/icons-react";
import { flattenTree, spanColor, formatDuration } from "./ExecutionTimelineUtils";

const LABEL_WIDTH = 180;
const ROW_HEIGHT = 28;
const ROW_GAP = 2;
const MIN_BAR_WIDTH = 3;
const INDENT_PX = 16;
const AXIS_HEIGHT = 24;

function TimeAxis({ totalDurationMs, timelineWidth }) {
  // Aim for ~5-8 tick marks
  const tickCount = Math.min(8, Math.max(3, Math.floor(timelineWidth / 80)));
  const tickInterval = totalDurationMs / tickCount;
  const ticks = [];
  for (let i = 0; i <= tickCount; i++) {
    ticks.push(i * tickInterval);
  }

  return (
    <div
      style={{
        position: "relative",
        height: AXIS_HEIGHT,
        marginLeft: LABEL_WIDTH,
        borderBottom: "1px solid var(--mantine-color-gray-3)",
      }}
    >
      {ticks.map((t) => {
        const left = (t / totalDurationMs) * 100;
        return (
          <div key={t} style={{ position: "absolute", left: `${left}%` }}>
            <div
              style={{
                position: "absolute",
                top: 0,
                width: 1,
                height: AXIS_HEIGHT,
                backgroundColor: "var(--mantine-color-gray-3)",
              }}
            />
            <Text
              size="10px"
              c="dimmed"
              style={{
                position: "absolute",
                top: 4,
                left: 4,
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              {formatDuration(Math.round(t))}
            </Text>
          </div>
        );
      })}
    </div>
  );
}

function SpanRow({ span, totalDurationMs }) {
  const leftPct = (span.startOffset / totalDurationMs) * 100;
  const widthPct = (span.duration / totalDurationMs) * 100;
  const color = spanColor(span);
  const indent = (span.depth || 0) * INDENT_PX;

  const tooltipLines = [span.label];
  tooltipLines.push(`Duration: ${formatDuration(span.duration)}`);
  if (span.type === "inference" && span.routerOverheadMs > 0) {
    tooltipLines.push(
      `Provider time: ${formatDuration(span.providerDurationMs)} · Router overhead: ${formatDuration(span.routerOverheadMs)}`
    );
  }
  if (span.tokensIn || span.tokensOut) {
    tooltipLines.push(`Tokens: ${span.tokensIn ?? 0} in / ${span.tokensOut ?? 0} out`);
  }
  if (span.creditsUsed) {
    tooltipLines.push(`Cost: ${(span.creditsUsed / 10_000).toFixed(4)}c`);
  }
  if (span.errorMessage) {
    tooltipLines.push(`Error: ${span.errorMessage}`);
  }

  const isInference = span.type === "inference";

  return (
    <div
      style={{
        display: "flex",
        height: ROW_HEIGHT,
        alignItems: "center",
      }}
    >
      {/* Label column */}
      <div
        style={{
          width: LABEL_WIDTH,
          minWidth: LABEL_WIDTH,
          paddingLeft: indent + 4,
          overflow: "hidden",
        }}
      >
        <Text
          size="xs"
          truncate
          c={isInference ? "dimmed" : undefined}
          fs={isInference ? "italic" : undefined}
        >
          {span.type === "agent" && span.depth > 0 ? "  " : ""}
          {span.label}
        </Text>
      </div>
      {/* Timeline bar area */}
      <div style={{ flex: 1, position: "relative", height: "100%" }}>
        <MantineTooltip
          label={tooltipLines.join("\n")}
          multiline
          withArrow
          position="top"
          style={{ whiteSpace: "pre-line" }}
        >
          <div
            style={{
              position: "absolute",
              left: `${leftPct}%`,
              width: `max(${MIN_BAR_WIDTH}px, ${widthPct}%)`,
              top: 4,
              bottom: 4,
              backgroundColor: color,
              opacity: isInference ? 0.45 : 0.85,
              borderRadius: 3,
              cursor: "default",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = isInference ? "0.45" : "0.85";
            }}
          />
        </MantineTooltip>
        {/* Duration label on bar if wide enough */}
        {widthPct > 6 && (
          <Text
            size="10px"
            c="white"
            fw={500}
            style={{
              position: "absolute",
              left: `${leftPct}%`,
              top: "50%",
              transform: "translateY(-50%)",
              paddingLeft: 6,
              pointerEvents: "none",
              textShadow: "0 0 2px rgba(0,0,0,0.4)",
              whiteSpace: "nowrap",
            }}
          >
            {formatDuration(span.duration)}
          </Text>
        )}
      </div>
    </div>
  );
}

function Legend() {
  const items = [
    { color: "#4c6ef5", label: "Agent" },
    { color: "#748ffc", label: "LLM Inference", opacity: 0.45 },
    { color: "#15aabf", label: "External Tool" },
    { color: "#20c997", label: "Virtual Tool" },
    { color: "#9775fa", label: "Sub-Agent Spawn" },
    { color: "#fa5252", label: "Failed" },
  ];

  return (
    <Group gap="md">
      {items.map((item) => (
        <Group gap={4} key={item.label}>
          <Box
            style={{
              width: 14,
              height: 10,
              backgroundColor: item.color,
              opacity: item.opacity ?? 0.85,
              borderRadius: 2,
            }}
          />
          <Text size="xs" c="dimmed">
            {item.label}
          </Text>
        </Group>
      ))}
    </Group>
  );
}

export default function ExecutionTimeline({ tree, loading, error }) {
  const { spans, totalDurationMs } = useMemo(
    () => (tree ? flattenTree(tree) : { spans: [], totalDurationMs: 0 }),
    [tree]
  );

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        {error}
      </Alert>
    );
  }

  if (!tree) {
    return (
      <Alert color="gray" icon={<IconBrain size={16} />}>
        No agent execution data available for this request.
      </Alert>
    );
  }

  if (spans.length === 0 || totalDurationMs === 0) {
    return (
      <Alert color="gray" icon={<IconBrain size={16} />}>
        No timing data available.
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Paper withBorder radius="sm" p="sm">
        <ScrollArea>
          <div style={{ minWidth: 600 }}>
            <TimeAxis totalDurationMs={totalDurationMs} timelineWidth={600} />
            <div style={{ marginTop: 4 }}>
              {spans.map((span) => (
                <div key={span.id} style={{ marginBottom: ROW_GAP }}>
                  <SpanRow span={span} totalDurationMs={totalDurationMs} />
                </div>
              ))}
            </div>
          </div>
        </ScrollArea>
      </Paper>
      <Legend />
    </Stack>
  );
}
